import {
  CalendarCheck,
  ClipboardList,
  Users,
  Package,
  Wrench,
  ReceiptText,
  Repeat,
} from 'lucide-react';

export const modules = [
  {
    id: 'appointments',
    icon: CalendarCheck,
    name: 'Appointments',
    summary: 'Booking, confirmations, and reminders that run themselves.',
    points: [
      'Set weekly availability once — the system handles slot durations, double-booking prevention, and business hours automatically.',
      'Customers book by phone (AI voice bot), WhatsApp, or a quick call to your office — every channel lands in the same calendar.',
      'Automatic confirmations and reminders before every visit, so no-shows and missed jobs drop.',
      'Reschedule or cancel in a couple of taps, with every technician’s day staying in sync.',
    ],
  },
  {
    id: 'workforce',
    icon: ClipboardList,
    name: 'Workforce Management',
    summary: 'Every technician, every task, tracked from assignment to sign-off.',
    points: [
      'Assign jobs, routes, and daily tasks to the right technician based on location and workload.',
      'Field staff update job status in real time — accepted, in-progress, completed — from their phone via Tecnvi Ongo.',
      'Photo proof and completion notes are attached to every closed job automatically, building a clean audit trail.',
      'Track attendance, completion rates, and performance per technician without spreadsheets.',
    ],
  },
  {
    id: 'customers',
    icon: Users,
    name: 'Customer Management',
    summary: 'One record per customer — every call, order, and service visit in one place.',
    points: [
      'Separate B2C and B2B pipelines, so individual homeowners and institutional clients are handled the way each needs.',
      'Full service and order history tied to every customer, so any team member can pick up where the last conversation left off.',
      'Document storage for contracts, tax forms, and compliance paperwork on B2B accounts.',
      'Customers are created automatically from calls, WhatsApp chats, or orders — no manual data entry.',
    ],
  },
  {
    id: 'products',
    icon: Package,
    name: 'Products & Inventory',
    summary: 'Parts, stock, and reorder points — always visible, never guessed.',
    points: [
      'Track stock levels, reorder points, and warehouse locations for every spare part or product you carry.',
      'Bulk upload your entire catalog from a spreadsheet, PDF, or photos — the system extracts the details for you.',
      'Multi-warehouse and stock-transfer support for teams operating across more than one location.',
      'Low-stock alerts mean a technician’s van is restocked before it becomes a missed job.',
    ],
  },
  {
    id: 'services',
    icon: Wrench,
    name: 'Services Catalog',
    summary: 'Every service you offer, priced and ready to quote instantly.',
    points: [
      'Maintain a catalog of every repair, installation, or maintenance service you offer, with consistent pricing.',
      'Link services directly to appointments and orders, so a booked job flows straight into a priced work order.',
      'Bulk import and AI-assisted catalog cleanup, the same as the products catalog.',
    ],
  },
  {
    id: 'orders',
    icon: ReceiptText,
    name: 'Orders',
    summary: 'From request to invoice, without re-typing anything twice.',
    points: [
      'Orders can come from a phone call, a WhatsApp message, or manual entry — all landing in one queue.',
      'Assign orders to technicians as fulfillment tasks, with stock automatically adjusted on completion.',
      'Generate dispatch notes and invoices directly from the order, with no separate billing step.',
    ],
  },
  {
    id: 'subscriptions',
    icon: Repeat,
    name: 'Subscription Orders',
    summary: 'Recurring service and AMC contracts that manage themselves.',
    points: [
      'Turn any AMC, refill round, or recurring service into a subscription — the system generates every future visit automatically.',
      'Pause and resume subscriptions for holidays or downtime without cancelling the whole contract.',
      'Monthly billing consolidates automatically from every completed visit — no manual invoice math.',
      'Nothing falls through the cracks: unattended visits are safely closed out by an automatic nightly check.',
    ],
  },
];

export const apps = [
  {
    id: 'ongo',
    name: 'Tecnvi Ongo',
    audience: 'For your technicians and field crews',
    tagline: 'The field app that keeps every job on record.',
    description:
      'Tecnvi Ongo is the mobile companion for your workforce. Technicians, delivery staff, and site crews log in to see exactly what’s assigned to them today — the job, the customer, the location, and what needs doing.',
    points: [
      'See today’s assigned jobs, routes, or site visits in one list.',
      'Update status live: start, in-progress, completed, or cancelled with a reason.',
      'Attach photos and notes as proof of work — building a real inspection or completion report on every job.',
      'Track daily earnings and job history without calling the office to ask.',
    ],
  },
  {
    id: 'connect',
    name: 'Tecnvi Connect',
    audience: 'For your customers',
    tagline: 'Let customers help themselves — without picking up the phone.',
    description:
      'Tecnvi Connect gives your customers a direct line to your business — to place an order, book an appointment, check a service status, or pay a bill — over WhatsApp, a call, or a simple web link, without waiting on hold.',
    points: [
      'Book, reschedule, or cancel an appointment without calling in.',
      'Browse your product or service catalog and place an order directly.',
      'Get real-time order and service status updates automatically.',
      'Verify identity, sign agreements, and receive bills — all self-service.',
    ],
  },
];
